// "Continue with Facebook" — shared by the sign-up and log-in pages.
//
// While the Meta app was in Development mode (only the app's admins and
// testers can log in; everyone else gets a Facebook error) it showed only:
//   - when REACT_APP_FACEBOOK_LOGIN=true (local), or
//   - in a browser that has opened any page with ?fbtest=1 (remembered), so
//     it can be tried on the live site, e.g. inside the Facebook app.
// ?fbtest=0 turns it off again in that browser.

import firebase from 'firebase/compat/app';
import { FacebookAuthProvider } from '../Firebase';

const KEY = 'fbLoginTest';

// On for everyone once the Meta app is Live (published, confirmed 29 Oct 2026). Set back
// to false to hide the button again (?fbtest=1 still shows it).
const FACEBOOK_LOGIN_FOR_EVERYONE = true;

export const isFacebookLoginOn = () => {
  if (FACEBOOK_LOGIN_FOR_EVERYONE || process.env.REACT_APP_FACEBOOK_LOGIN === 'true') return true;
  try {
    const flag = new URLSearchParams(window.location.search).get('fbtest');
    if (flag === '1') localStorage.setItem(KEY, '1');
    if (flag === '0') localStorage.removeItem(KEY);
    return localStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
};

// Facebook accounts made with a phone number have no email (Facebook never
// shares the number). They can still join; onboarding step 1 asks for an
// email (ProfileBasics). Firebase may also leave user.email empty and keep
// it on the provider entry.
export const facebookEmail = (user) =>
  user?.email || user?.providerData?.find(p => p.providerId === 'facebook.com')?.email || null;

// The email already has an account (made with Google or email): returns that
// email ('' if unknown) and holds Facebook for linking — the page then shows
// <FacebookLinkNotice>. null for any other error.
export const facebookLinkEmail = (error) => {
  if (error?.code !== 'auth/account-exists-with-different-credential') return null;
  holdFacebookForLinking(error);
  return error.email || error.customData?.email || '';
};

// A friendly message for a failed Facebook sign-in, or null when there's
// nothing to say (they closed the pop-up).
export const facebookErrorMessage = (error) => {
  switch (error?.code) {
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return null;
    case 'auth/account-exists-with-different-credential': {
      // Pages show <FacebookLinkNotice> instead (facebookLinkEmail).
      holdFacebookForLinking(error);
      const email = error.email || error.customData?.email;
      return `You've already joined${email ? ` as ${email}` : ''}. Continue with Google to sign in, and we'll add Facebook to your account. (Joined with email? Use that instead.)`;
    }
    case 'auth/popup-blocked':
      return 'Your browser blocked the Facebook window. Please allow pop-ups for this site, or sign up with your email.';
    case 'auth/unauthorized-domain':
      return 'Sign-in is not configured for this domain. Please contact support.';
    default:
      return 'Facebook sign-in failed. Please try again, or use your email.';
  }
};

// Linking Facebook to an account made with Google. Facebook sign-in fails
// with account-exists-with-different-credential when the email already has
// an account; we keep Facebook's credential here, and the page's Google
// sign-in calls linkHeldFacebook(user) once it succeeds — then either button
// works from then on. Only linked when the Google account has the same email.
// Kept in sessionStorage too: with sign-in by redirect (utils/socialSignIn.js)
// the page reloads between the Facebook attempt and the Google sign-in.
const HELD_KEY = 'icHeldFacebook';
let heldFacebook = null;

const holdFacebookForLinking = (error) => {
  const credential = error?.credential
    || (typeof FacebookAuthProvider.credentialFromError === 'function' ? FacebookAuthProvider.credentialFromError(error) : null);
  const email = error?.email || error?.customData?.email;
  heldFacebook = credential && email ? { credential, email: email.toLowerCase() } : null;
  try {
    if (heldFacebook) sessionStorage.setItem(HELD_KEY, JSON.stringify({ credential: credential.toJSON(), email: heldFacebook.email, at: Date.now() }));
    else sessionStorage.removeItem(HELD_KEY);
  } catch { /* storage blocked: linking works on this page only */ }
};

const takeHeldFacebook = () => {
  let held = heldFacebook;
  heldFacebook = null;
  try {
    const saved = JSON.parse(sessionStorage.getItem(HELD_KEY) || 'null');
    sessionStorage.removeItem(HELD_KEY);
    if (!held && saved && Date.now() - (saved.at || 0) < 30 * 60 * 1000) {
      held = { credential: firebase.auth.AuthCredential.fromJSON(saved.credential), email: saved.email };
    }
  } catch { /* nothing saved */ }
  return held;
};

export const clearHeldFacebook = () => {
  heldFacebook = null;
  try { sessionStorage.removeItem(HELD_KEY); } catch { /* ignore */ }
};

export const linkHeldFacebook = async (user) => {
  if (!user) return false;
  const held = takeHeldFacebook();
  if (!held?.credential) return false;
  if (String(user.email || '').toLowerCase() !== held.email) return false;
  try {
    await user.linkWithCredential(held.credential);
    console.log('[facebookLogin] Facebook linked to', held.email);
    return true;
  } catch (err) {
    // Already linked, or that Facebook is on another account — nothing to do.
    console.warn('[facebookLogin] link failed:', err.code || err.message);
    return false;
  }
};
